import Button from '@/shared/Button'
import FormItem from '@/shared/FormCreator/FormItem'
import Download from '@mui/icons-material/Download'
import Preview from '@mui/icons-material/Preview'
import Box from '@mui/material/Box'
import Container from '@mui/material/Container'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'

import debounce from '@mui/material/utils/debounce'
import isEmpty from 'lodash/isEmpty'
import unionBy from 'lodash/unionBy'
import moment from 'moment'
import { useForm } from 'react-hook-form'

import CourseReportDocument from '@/components/ReportPDF/CourseReportDocument'
import ReportPreview from '@/components/ReportPDF/ReportPreview'
import { yupResolver } from '@hookform/resolvers/yup'
import * as Yup from 'yup'

import { PDFDownloadLink, usePDF } from '@react-pdf/renderer'
import * as ReactDOMClient from 'react-dom/client'

import PermissionsController from '@/components/PermissionsController'
import CourseReportSample from '@/components/ReportPDF/CourseReportSample'
import { generate } from '@/components/ReportWord/CourseReportWordGenerator'
import { Packer } from 'docx'
import { saveAs } from 'file-saver'

const mapStateToProps = ({ report, loading }) => ({
  reportData: report.courseReportData,
  isCampaignsLoading: loading['campaign/getCampaigns'],
  isReportLoading: loading['report/getCourseReportData'],
})

const inputConfig = {
  endAdornment: args => {
    const { value, inputProps: { maxLength } = {} } = args

    return (
      maxLength && (
        <Typography
          variant="caption"
          sx={{ color: '#00000040', fontSize: '14px !important' }}
        >{`${value.length}/${maxLength}`}</Typography>
      )
    )
  },
}

const DownloadElement = ({ url, loading, setIsDownloading }) => {
  const ref = useRef()

  useEffect(() => {
    if (url && !loading) {
      ref.current?.click?.()
      setIsDownloading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, loading])

  return <div ref={ref} />
}

const FirstRenderDoc = ({ download, onDownload, ...props }) => {
  usePDF({
    document: <CourseReportDocument {...props} />,
  })

  return null
}

const loadFirstRenderDoc = ({ onRender, ...props }) => {
  const helperDiv = document.createElement('tmp')
  const newRoot = ReactDOMClient.createRoot(helperDiv)

  const doc = (
    <FirstRenderDoc
      {...props}
      onRender={onRenderProps => {
        onRender?.(onRenderProps)
        newRoot?.unmount()
        helperDiv?.remove()
      }}
    />
  )

  newRoot.render(<React.StrictMode>{doc}</React.StrictMode>)
}

const CourseReportPage = ({ dispatch, isCampaignsLoading, isReportLoading, reportData }) => {
  const { t } = useTranslation()

  const phishCampaignsSearchInputRef = useRef()

  const [showSample, setShowSample] = useState(false)
  const [showPreview, setShowPreview] = useState(false)
  const [phishCampaignsSearchInput, setPhishCampaignsSearchInput] = useState('')
  const [phishCampaignsSearchResults, setPhishCampaignsSearchResults] = useState({})
  const [phishCampaignsPage, setPhishCampaignsPage] = useState(0)

  const [pdfDoc, setPdfDoc] = useState()
  const [isDownloading, setIsDownloading] = useState()
  const [isDownloadingWord, setIsDownloadingWord] = useState()

  const { data: phishCampaignsSearchResultsData = [], paging: phishCampaignsSearchResultsPaging } =
    phishCampaignsSearchResults

  const validations = {
    primaryCompanyFullname: Yup.string().required(),
    primaryCompanyNickname: Yup.string().required(),
    secondaryCompanyFullname: Yup.string().required(),
    secondaryCompanyNickname: Yup.string().required(),
    course: Yup.object().required(),
    version: Yup.string().required(),
  }

  const form = useForm({
    resolver: yupResolver(Yup.object().shape(validations)),
    mode: 'onBlur',
  })

  const {
    control,
    handleSubmit,
    watch,
    trigger,
    setValue,
    // eslint-disable-next-line no-unused-vars
    formState: { errors },
  } = form

  const phishType = watch('phishType')

  useEffect(() => {
    setValue('serviceCategory', t(`general.${phishType === 'M' ? 'mail' : 'sms'}`))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phishType])

  // fetch email templates START
  const fetchPhishCampaigns = useCallback(() => {
    dispatch({
      type: 'report/getReportTrainingCamps',
      payload: {
        searchCampName: phishCampaignsSearchInputRef.current,
        pageIndex: phishCampaignsPage,
        pageSize: 10,
      },
      skipPut: true,
      successCallback: response => {
        const { data: responseData = [], paging: responsePaging = {} } = response || {}
        const { pageIndex } = responsePaging

        const newData = responseData.map(d => ({
          id: d.trainingCampId,
          label: d.trainingCampName,
          data: d,
        }))

        setPhishCampaignsSearchResults(s => ({
          data: pageIndex > 0 ? unionBy(s.data || [], newData, 'id') : newData,
          paging: responsePaging,
        }))
      },
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phishCampaignsPage])

  const loadMorePhishCampaignsDebounce = debounce(() => {
    setPhishCampaignsPage(p => p + 1)
  }, 400)

  const fetchPhishCampaignsDebounce = useMemo(
    () =>
      debounce(() => {
        fetchPhishCampaigns()
      }, 100),
    [fetchPhishCampaigns],
  )

  const campSearchDebounce = useCallback(
    () =>
      debounce(({ value, reason }) => {
        phishCampaignsSearchInputRef.current = value

        if (['input', 'clear'].includes(reason)) {
          setPhishCampaignsPage(0)
          setPhishCampaignsSearchInput(value)
        }
      }, 400),
    [],
  )

  useEffect(() => {
    fetchPhishCampaignsDebounce()
  }, [phishCampaignsSearchInput, phishCampaignsPage, fetchPhishCampaignsDebounce])
  // fetch email templates END

  // email templates clean up START
  useEffect(() => {
    setPhishCampaignsSearchResults({})
    setPhishCampaignsPage(0)
  }, [])
  // email templates clean up END

  const onSubmit = (data, callback) => {
    const {
      course,
      generateDate,
      primaryCompanyFullname,
      primaryCompanyNickname,
      secondaryCompanyFullname,
      secondaryCompanyNickname,
      serviceCategory,
      coursePeriodFrom,
      coursePeriodTo,
      version,
    } = data
    const { id: courseId, label: courseName } = course || {}

    setPdfDoc()

    if (course) {
      dispatch({
        type: 'report/getCourseReportData',
        payload: {
          courseId,
          courseName,
          firstPartyAbbreviation: primaryCompanyNickname,
          secondPartyAbbreviation: secondaryCompanyNickname,
          firstPartyFullName: primaryCompanyFullname,
          secondPartyFullName: secondaryCompanyFullname,
          projectType: serviceCategory,
          version,
          reportDate: moment(generateDate, 'YYYY-MM-DD'),
          courseStartDate: moment(coursePeriodFrom, 'YYYY-MM-DD'),
          courseEndDate: moment(coursePeriodTo, 'YYYY-MM-DD'),
        },
        successCallback: callback,
      })
    }
  }

  useEffect(() => {
    trigger?.()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const generateWordDocument = async () => {
    const wordDoc = await generate({ reportData: { ...reportData, metaData: reportData } })

    Packer.toBlob(wordDoc).then(blob => {
      saveAs(blob, 'report.docx')
      setIsDownloadingWord(false)
    })
  }

  useEffect(() => {
    if (isDownloadingWord && reportData) {
      generateWordDocument()

      return
    }

    if (!pdfDoc && reportData) {
      loadFirstRenderDoc({
        reportData: { ...reportData, metaData: reportData },
        onRender: ({ pageData }) => {
          setPdfDoc(
            <CourseReportDocument
              pageData={pageData}
              reportData={{ ...reportData, metaData: reportData }}
            />,
          )
        },
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pdfDoc, isDownloadingWord, reportData])

  return (
    <Container>
      <Stack direction="row" justifyContent="flex-end" spacing={1}>
        <Button
          disabled={isReportLoading || isDownloading || isDownloadingWord}
          variant="text"
          color="secondary_80"
          endIcon={<Preview />}
          onClick={() => {
            setShowSample(true)
          }}
        >
          {t('general.preview')}
        </Button>

        <Button
          loading={isReportLoading || isDownloading || isDownloadingWord}
          disabled={!isEmpty(errors) || isReportLoading || isDownloading || isDownloadingWord}
          variant="text"
          color="secondary_80"
          endIcon={<Preview />}
          onClick={handleSubmit(data => {
            onSubmit(data, () => {
              setShowPreview(true)
            })
          })}
        >
          {t('general.review_document')}
        </Button>

        <PermissionsController permission="courseReport_download">
          <Button
            onClick={handleSubmit(data => {
              onSubmit(data, () => {
                setIsDownloading(true)
              })
            })}
            loading={isReportLoading || isDownloading || isDownloadingWord}
            disabled={!isEmpty(errors) || isReportLoading || isDownloading || isDownloadingWord}
            variant="text"
            color="secondary_80"
            endIcon={<Download />}
          >
            {t('general.download_pdf')}
          </Button>
        </PermissionsController>

        <PermissionsController permission="courseReport_download">
          <Button
            onClick={handleSubmit(data => {
              onSubmit(data, () => {
                setIsDownloadingWord(true)
              })
            })}
            loading={isReportLoading || isDownloading || isDownloadingWord}
            disabled={!isEmpty(errors) || isReportLoading || isDownloading || isDownloadingWord}
            variant="text"
            color="secondary_80"
            endIcon={<Download />}
          >
            {t('general.download_word')}
          </Button>
        </PermissionsController>

        {isDownloading && pdfDoc && (
          <PDFDownloadLink document={pdfDoc} fileName="report.pdf">
            {props => <DownloadElement {...props} setIsDownloading={setIsDownloading} />}
          </PDFDownloadLink>
        )}
      </Stack>

      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack spacing={2} mb={4}>
          <Stack direction={'row'} spacing={2}>
            <FormItem
              defaultValue={'M'}
              label={t('general.service_category')}
              name="phishType"
              type="select"
              control={control}
              inputConfig={{
                sx: { width: 100 },
                options: [
                  { value: 'M', label: t('general.mail') },
                  { value: 'S', label: t('general.sms') },
                ],
              }}
            />

            <FormItem
              defaultValue={''}
              label={t('general.training_course_name')}
              name="course"
              type="autocomplete"
              control={control}
              inputConfig={{
                sx: { width: 250 },
                onInputChange: (_, value, reason) => {
                  campSearchDebounce()({ value, reason })
                },
                onFocus: () => {
                  fetchPhishCampaignsDebounce()
                },
                onLoadMore: () => {
                  const { hasNext } = phishCampaignsSearchResultsPaging

                  if (!isCampaignsLoading && hasNext) {
                    loadMorePhishCampaignsDebounce()
                  }
                },
                options: phishCampaignsSearchResultsData,
                filterOptions: opts => opts,
                isOptionEqualToValue: (option, value) => option.id === value.id,
                renderOption: (renderProps, option) => (
                  <Box component="li" {...renderProps} key={option.id}>
                    {option.label}
                  </Box>
                ),
              }}
            />
          </Stack>

          <Stack direction={'row'} spacing={2}>
            <FormItem
              defaultValue=""
              label={t('general.primary_company_nickname')}
              name="primaryCompanyNickname"
              control={control}
              inputConfig={{ ...inputConfig, inputProps: { maxLength: 40 } }}
            />

            <FormItem
              defaultValue=""
              label={t('general.primary_company_fullname')}
              name="primaryCompanyFullname"
              control={control}
              inputConfig={{ ...inputConfig, inputProps: { maxLength: 40 } }}
            />
          </Stack>

          <Stack direction={'row'} spacing={2}>
            <FormItem
              defaultValue=""
              label={t('general.secondary_company_nickname')}
              name="secondaryCompanyNickname"
              control={control}
              inputConfig={{ ...inputConfig, inputProps: { maxLength: 40 } }}
            />
            <FormItem
              defaultValue=""
              label={t('general.secondary_company_fullname')}
              name="secondaryCompanyFullname"
              control={control}
              inputConfig={{ ...inputConfig, inputProps: { maxLength: 40 } }}
            />
          </Stack>

          <Stack direction={'row'} spacing={2}>
            <FormItem
              defaultValue=""
              label={t('general.generate_version')}
              name="version"
              control={control}
              inputConfig={{ ...inputConfig, inputProps: { maxLength: 40 } }}
            />
          </Stack>

          <FormItem
            label={t('general.generate_report_date')}
            name="generateDate"
            defaultValue={moment().toDate()}
            type="date"
            control={control}
            errors={errors}
          />

          <Stack direction="row" alignItems="center" spacing={1}>
            <FormItem
              label={t('general.training_course_period')}
              name="coursePeriodFrom"
              defaultValue={moment().toDate()}
              type="date"
              control={control}
            />
            <Typography>{t('general.to')}</Typography>
            <FormItem
              name="coursePeriodTo"
              defaultValue={moment().toDate()}
              type="date"
              control={control}
            />
          </Stack>
        </Stack>
      </form>

      {showSample && (
        <ReportPreview
          content={<CourseReportSample />}
          onClose={() => {
            setShowSample(false)
          }}
        />
      )}

      {showPreview && (
        <ReportPreview
          document={pdfDoc}
          onClose={() => {
            setPdfDoc()
            setShowPreview(false)
          }}
        />
      )}
    </Container>
  )
}

export default connect(mapStateToProps)(CourseReportPage)
